import { EventStatus } from '../api/types';

interface MinimalEvent {
  sport_name?: string;
  sport_id?: string;
}

/**
 * True when the status text marks a match as no longer in play
 * (finished / closed). Callers should show the start date instead of a clock.
 */
export function isMatchEnded(status?: EventStatus | null): boolean {
  const st = (status?.match_status_text || '').toLowerCase().trim();
  return [
    'ended', 'closed', 'finished', 'ft', 'beendet', 'bitti', 'ms',
    'aet', 'ap', 'after extra time', 'after penalties',
  ].includes(st);
}

/**
 * Formats the live period / minute label for a match (e.g. "45+2'", "HT",
 * "90'", "3/9"). Ported from LivePage's formatClock so the bet-ticket detail
 * shows the exact same live indicator the live list uses.
 */
export function formatLiveClock(
  status: EventStatus,
  event: MinimalEvent,
  t?: (k: string) => string
): string {
  const sport = event?.sport_name;
  const sportId = event?.sport_id;

  if (sport === 'Baseball' || sportId === 'sr:sport:3') {
    const text = status?.match_status_text || '';
    const numMatch = text.match(/\d+/);
    if (numMatch) return `${numMatch[0]}/9`;
    if (text.toLowerCase().includes('break')) {
      const ps = status?.period_scores;
      if (ps && ps.length > 0) return `${ps.length}/9`;
      return 'Break';
    }
    if (text) return text;
  }

  const useStatusText =
    sport === 'Basketball' || sport === 'Tennis' || sport === 'Table Tennis' ||
    sport === 'Volleyball' || sportId === 'sr:sport:2' || sportId === 'sr:sport:5' ||
    sportId === 'sr:sport:20' || sportId === 'sr:sport:23';

  const normalize = (s: string) =>
    s.replace(/\b(?:1st|first)\b/gi, '1.')
      .replace(/\b(?:2nd|second)\b/gi, '2.')
      .replace(/\b(?:3rd|third)\b/gi, '3.')
      .replace(/\b(?:4th|fourth)\b/gi, '4.')
      .replace(/\b(\d+)(st|nd|rd|th)\b/gi, '$1.');

  if (useStatusText && status?.match_status_text) {
    return normalize(status.match_status_text);
  }

  const stLower = (status?.match_status_text || '').toLowerCase();
  if (stLower === 'halftime' || stLower === 'halbzeit' || stLower === 'devre arası' || stLower === 'ht') {
    return t ? t('live.ht') : 'HT';
  }
  if (stLower === 'ended' || stLower === 'closed' || stLower === 'finished' || stLower === 'ft' || stLower === 'beendet' || stLower === 'bitti' || stLower === 'ms') {
    return t ? t('live.ft') : 'FT';
  }

  if (status?.clock) {
    const minStr = status.clock.split(':')[0];
    if (minStr.includes('+')) return minStr + "'";
    const minNum = parseInt(minStr, 10);
    if (!isNaN(minNum)) {
      const displayMin = minNum + 1;

      if (sport === 'Soccer' || sportId === 'sr:sport:1') {
        const matchStatusCode = (status as any)?.match_status;
        const isFirstHalf = stLower.includes('1.') || stLower.includes('1st') || stLower.includes('first') || stLower.includes('ilk') || stLower === '1' || matchStatusCode === '6' || matchStatusCode === 6;
        const isExtraTime = stLower.includes('et') || stLower.includes('extra') || stLower.includes('uzatma') || stLower.includes('verläng') || stLower.includes('verlaeng') || stLower.includes('overtime') || matchStatusCode === '41' || matchStatusCode === '42' || matchStatusCode === 41 || matchStatusCode === 42;

        const is90 = minStr === '90';
        const is45 = minStr === '45';

        if ((is90 && !isExtraTime && !isFirstHalf) || (is45 && isFirstHalf)) {
          const baseMin = is90 ? 90 : 45;
          const stoppage = status?.stoppage_time;
          if (stoppage && stoppage !== '0:00' && typeof stoppage === 'string') {
            const parts = stoppage.split(':');
            if (parts.length === 2) {
              const sMin = parseInt(parts[0], 10);
              const sSec = parseInt(parts[1], 10);
              if (!isNaN(sMin) && !isNaN(sSec)) {
                let added = sMin;
                if (sSec > 0 || sMin === 0) added += 1;
                return `${baseMin}+${added}'`;
              }
            }
          }
          return `${baseMin}'`;
        }

        if (isFirstHalf && displayMin > 45 && !isExtraTime) return `45+${displayMin - 45}'`;
        if (displayMin > 90 && !isExtraTime && !isFirstHalf) return `90+${displayMin - 90}'`;
      }

      return displayMin + "'";
    }
    return minStr + "'";
  }

  if (status?.match_status_text) return normalize(status.match_status_text);
  return t ? t('live.live') : 'Live';
}
